import { Fragment, useState, useEffect } from 'react';
import { Menu, Transition } from '@headlessui/react';
import {
  Bars3Icon,
  BellIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { Avatar } from '@/components/common/Avatar';
import { ProjectSelector } from '@/components/projects/ProjectSelector';
import { OrgSwitcher } from "../ui/OrgSwitcher";
import { cn } from '@/utils/cn';
import { getNotifications } from '@/services/notificationService';

export function Header() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const toggleSidebar = useUIStore((state) => state.toggleSidebar);
  const [notifications, setNotifications] = useState<any[]>([]);
  // const [isNotificationOpen, setIsNotificationOpen] = useState(false);

  useEffect(() => {
    fetchNotifications();

    const interval = setInterval(() => {
      fetchNotifications();
    }, 10000);

    return () => clearInterval(interval);
  }, []);


  const fetchNotifications = async () => {
    try {
      const notificationsData = await getNotifications();
      setNotifications(notificationsData || []);
      console.log('Fetched notifications:', notificationsData);
    } catch (error) {
      console.error(error);
      setNotifications([]);
    }
  };

  // const handleNotificationOpen = async () => {
  //   setIsNotificationOpen(true);

  //   try {
  //     await markNotificationsRead();
  //     setNotifications([]);
  //   } catch (error) {
  //     console.error(error);
  //   }
  // };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 z-10">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left side */}
          <div className="flex items-center gap-4">
            <button
              type="button"
              className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-md p-2"
              onClick={toggleSidebar}
            >
              <Bars3Icon className="h-6 w-6" />
            </button>

            <div className="hidden md:block">
              <h1 className="text-xl font-semibold text-gray-900">
                CORTEX
              </h1>
            </div>

            {/* Project Selector */}
            <div className="hidden lg:block ml-4">
              <ProjectSelector />
            </div>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-4">
            {/* Notifications */}
            <Menu as="div" className="relative">
              <Menu.Button className="relative p-2 text-gray-500 hover:text-gray-700">
                <BellIcon className="h-6 w-6" />

                {notifications?.length > 0 && (
                  <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full" />
                )}
              </Menu.Button>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 mt-2 w-96 bg-white rounded-md shadow-lg border z-50 max-h-[500px] overflow-hidden">
                  <div className="p-4 border-b font-semibold">
                    Notifications
                  </div>

                  <div className="max-h-96 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-4 text-sm text-gray-500">
                        No notifications
                      </div>
                    ) : (
                      notifications?.map((notification: any) => (
                        <div
                          key={notification.id}
                          className="p-4 border-b hover:bg-gray-50"
                        >
                          <div className="font-medium text-sm">
                            {notification.title}
                          </div>

                          <div className="text-sm text-gray-600 mt-1">
                            {notification.message}
                          </div>

                          <div className="text-xs text-gray-400 mt-2">
                            {new Date(notification.created_at).toLocaleString()}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>

            {/* Organization Switcher */}
            <OrgSwitcher />


            {/* User menu */}
            <Menu as="div" className="relative">
              <Menu.Button className="flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-md p-1">
                <Avatar
                  src={user?.avatar}
                  name={user?.name}
                  size="sm"
                />
                <span className="hidden md:block text-sm font-medium text-gray-700">
                  {user?.name}
                </span>
              </Menu.Button>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">
                      {user?.name}
                    </p>
                    <p className="text-sm text-gray-500 truncate">
                      {user?.email}
                    </p>
                  </div>

                  <div className="py-1">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => navigate('/profile')}
                          className={cn(
                            'flex items-center gap-3 w-full px-4 py-2 text-sm',
                            active
                              ? 'bg-gray-100 text-gray-900'
                              : 'text-gray-700'
                          )}
                        >
                          <UserCircleIcon className="h-5 w-5" />
                          Profile
                        </button>
                      )}
                    </Menu.Item>

                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => navigate('/settings')}
                          className={cn(
                            'flex items-center gap-3 w-full px-4 py-2 text-sm',
                            active
                              ? 'bg-gray-100 text-gray-900'
                              : 'text-gray-700'
                          )}
                        >
                          <Cog6ToothIcon className="h-5 w-5" />
                          Settings
                        </button>
                      )}
                    </Menu.Item>
                  </div>

                  <div className="py-1 border-t border-gray-100">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={logout}
                          className={cn(
                            'flex items-center gap-3 w-full px-4 py-2 text-sm',
                            active
                              ? 'bg-gray-100 text-danger-700'
                              : 'text-danger-600'
                          )}
                        >
                          <ArrowRightOnRectangleIcon className="h-5 w-5" />
                          Sign out
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
